import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const FAQ_ITEMS = [
  {
    id: "mirror",
    q: "How does the camera work here?",
    tag: "THE MIRROR PRINCIPLE",
    tagColor: "text-cyan",
    a: (
      <>
        It works just like a mirror on your wall. When you move in front of a mirror, you see your reflection respond in real time, but nothing is recorded or stored. The camera simply reflects your head and body movement into the 3D room.
      </>
    ),
  },
  {
    id: "photos",
    q: "Does the app save photos or video?",
    tag: "MOTION SENSING ONLY",
    tagColor: "text-lime",
    a: (
      <>
        <strong>No.</strong> The camera is used solely as a motion sensor. It measures the direction your head is turned (left, right, up, down) to steer your perspective, and coordinates your body posture with the avatar on screen. Think of your head movement like a joystick navigating a 3D space.
      </>
    ),
  },
  {
    id: "recording",
    q: "Is my video ever sent over the internet?",
    tag: "100% ON YOUR DEVICE",
    tagColor: "text-magenta",
    a: (
      <>
        <strong>No.</strong> All movement processing happens directly inside your web browser on your own computer or phone. Your video never leaves your device, and no video files are ever created or transmitted.
      </>
    ),
  },
  {
    id: "others",
    q: "Can anyone else see my session?",
    tag: "PRIVATE TO YOU",
    tagColor: "text-cyan",
    a: (
      <>
        <strong>No.</strong> This is a private, personal experience running solely in your own browser. There are no shared feeds or remote viewers—you are the only one in your room.
      </>
    ),
  },
  {
    id: "close",
    q: "What happens when I exit or close the tab?",
    tag: "INSTANT SHUTDOWN",
    tagColor: "text-lime",
    a: (
      <>
        Camera access stops immediately the moment you close the tab, leave the page, or click Exit. Your browser shuts off the camera light and releases your webcam instantly.
      </>
    ),
  },
  {
    id: "offline",
    q: "Can I verify that this works offline?",
    tag: "THE AIRPLANE MODE TEST",
    tagColor: "text-magenta",
    a: (
      <>
        Yes, you can test this easily yourself:
        <br />
        <strong>1.</strong> Load this web page in your browser.
        <br />
        <strong>2.</strong> Turn off your Wi-Fi or turn on <strong>Airplane Mode</strong>.
        <br />
        <strong>3.</strong> Click &ldquo;Enter the room&rdquo;.
        <br />
        All head tracking and movement will continue working smoothly offline, demonstrating that no internet connection is used to process your movement.
      </>
    ),
  },
];

export default function PrivacyModal({ isOpen, onClose, initialTab = "policy" }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [openFaqId, setOpenFaqId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      setOpenFaqId(null);
      const handleKeyDown = (e) => {
        if (e.key === "Escape") onClose();
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, initialTab, onClose]);

  const toggleFaq = (id) => {
    setOpenFaqId((prevId) => (prevId === id ? null : id));
  };

  if (!isOpen) return null;

  return (
    <div
      className="privacy-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-modal-title"
    >
      <div
        className="privacy-modal-card grain"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header bar */}
        <div className="privacy-modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="privacy-pulse-dot" />
            <span
              id="privacy-modal-title"
              className="font-mono"
              style={{
                fontSize: 12,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontWeight: 700,
                color: "var(--neon-cyan)",
              }}
            >
              Privacy &amp; Security
            </span>
          </div>
          <button
            onClick={onClose}
            className="privacy-close-btn font-mono"
            aria-label="Close modal"
          >
            ✕ CLOSE [ESC]
          </button>
        </div>

        {/* Navigation tabs */}
        <div className="privacy-tabs-nav font-mono">
          <button
            className={`privacy-tab-btn ${activeTab === "policy" ? "active" : ""}`}
            onClick={() => setActiveTab("policy")}
          >
            01 // PRIVACY POLICY
          </button>
          <button
            className={`privacy-tab-btn ${activeTab === "faq" ? "active" : ""}`}
            onClick={() => setActiveTab("faq")}
          >
            02 // FAQ
          </button>
          <button
            className={`privacy-tab-btn ${activeTab === "developers" ? "active" : ""}`}
            onClick={() => setActiveTab("developers")}
          >
            03 // TECHNICAL VERIFICATION
          </button>
        </div>

        {/* Modal content body */}
        <div className="privacy-modal-body">
          {/* TAB 1: PRIVACY POLICY (FIRST TAB) */}
          {activeTab === "policy" && (
            <div className="privacy-tab-pane font-mono">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <span className="text-muted" style={{ fontSize: 11, letterSpacing: "0.16em" }}>
                  EFFECTIVE DATE: SEPTEMBER 2026 // VERSION 1.0
                </span>
                <Link
                  to="/privacy"
                  onClick={onClose}
                  className="privacy-link"
                  style={{ fontSize: 11 }}
                >
                  Open Standalone Page →
                </Link>
              </div>

              <div className="privacy-policy-text">
                <h4 className="font-serif privacy-policy-heading">1. Scope &amp; Architecture</h4>
                <p>
                  Dard-e-Disco is a 100% client-side web experiment. There are no backend application servers, databases, third-party authentication systems, or cloud processing pipelines. All computation executes locally inside your browser runtime.
                </p>

                <h4 className="font-serif privacy-policy-heading">2. Video Hardware &amp; Sensor Data</h4>
                <p>
                  The application requests access to your device camera exclusively via the standard HTML5 <code>navigator.mediaDevices.getUserMedia</code> browser API.
                </p>
                <ul>
                  <li><strong>Purpose:</strong> Camera frames are used solely to estimate head-pose orientation (gaze direction) and joint coordinates to reflect your physical movements in the 3D room.</li>
                  <li><strong>Storage:</strong> No video images, facial landmarks, or movement coordinates are ever saved to local storage, indexedDB, cookies, or any remote filesystem.</li>
                  <li><strong>Transmission:</strong> No video or derived tracking coordinates are ever transmitted over any network protocol.</li>
                </ul>

                <h4 className="font-serif privacy-policy-heading">3. Cookies, Telemetry &amp; Analytics</h4>
                <p>
                  We do not use advertising networks, Google Analytics, Meta Pixels, session recording software (such as Hotjar or FullStory), or third-party tracking cookies. No profiling identifiers or telemetry tokens are generated.
                </p>

                <h4 className="font-serif privacy-policy-heading">4. Device Permissions &amp; Hardware Isolation</h4>
                <p>
                  Hardware permissions are restricted using the browser <code>Permissions-Policy</code> standard. Access to your microphone, geographic location, and payment APIs is explicitly disabled.
                </p>

                <h4 className="font-serif privacy-policy-heading">5. Data Retention &amp; Session Termination</h4>
                <p>
                  All in-memory video textures are discarded after each rendering frame. Closing or navigating away from the application tab immediately terminates the webcam capture stream and releases all allocated memory.
                </p>

                <h4 className="font-serif privacy-policy-heading">6. Source Integrity &amp; Verification</h4>
                <p>
                  All application bundles are delivered with full source transparency and zero obfuscated code for independent security inspection directly in your browser&apos;s Developer Tools.
                </p>
              </div>
            </div>
          )}

          {/* TAB 2: FAQ (SECOND TAB - ACCORDION STYLE) */}
          {activeTab === "faq" && (
            <div className="privacy-tab-pane font-mono">
              <div style={{ marginBottom: 16 }}>
                <div className="text-muted" style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase" }}>
                  FREQUENTLY ASKED QUESTIONS // CLICK QUESTION TO REVEAL ANSWER
                </div>
              </div>

              <div className="privacy-faq-accordion">
                {FAQ_ITEMS.map((item, index) => {
                  const isOpen = openFaqId === item.id;
                  return (
                    <div
                      key={item.id}
                      className={`privacy-faq-accordion-item ${isOpen ? "open" : ""}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(item.id)}
                        className="privacy-faq-accordion-btn font-mono"
                        aria-expanded={isOpen}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10, textAlign: "left" }}>
                          <span className="privacy-faq-number font-mono">{String(index + 1).padStart(2, "0")}</span>
                          <span className="privacy-faq-question">{item.q}</span>
                        </div>
                        <span className="privacy-faq-icon font-mono">{isOpen ? "−" : "+"}</span>
                      </button>

                      {isOpen && (
                        <div className="privacy-faq-accordion-content font-mono">
                          {item.tag && (
                            <div className={`privacy-card-tag font-mono ${item.tagColor}`} style={{ marginBottom: 6 }}>
                              // {item.tag}
                            </div>
                          )}
                          <div style={{ lineHeight: 1.65 }}>
                            {item.a}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 3: TECHNICAL VERIFICATION (THIRD TAB) */}
          {activeTab === "developers" && (
            <div className="privacy-tab-pane">
              <div className="privacy-highlight-box" style={{ borderColor: "var(--neon-magenta)" }}>
                <div className="privacy-box-title font-mono" style={{ color: "var(--neon-magenta)" }}>
                  BROWSER-ENFORCED CONTENT SECURITY POLICY (CSP)
                </div>
                <p className="privacy-text font-mono">
                  Trust shouldn’t rely on corporate promises. It is enforced at the browser engine level.
                  This application ships with a hard-locked <code>Content-Security-Policy</code> meta tag:
                </p>
                <pre className="privacy-code-block font-mono">
                  connect-src &apos;self&apos; ws: wss: blob: data:;
                </pre>
                <p className="privacy-text font-mono" style={{ marginTop: 8 }}>
                  The browser kernel will actively terminate and throw a fatal network error on any attempted <code>fetch()</code>, <code>XMLHttpRequest</code>, <code>WebSocket</code>, or <code>sendBeacon()</code> to external endpoints or third-party servers.
                </p>
              </div>

              <div className="privacy-grid-2" style={{ marginTop: 16 }}>
                <div className="privacy-card">
                  <div className="privacy-card-tag font-mono text-cyan">IN-MEMORY PIPELINE</div>
                  <h4 className="font-mono" style={{ fontSize: 13, margin: "6px 0 10px", color: "var(--ink)" }}>Local WebAssembly (WASM)</h4>
                  <p className="privacy-text font-mono">
                    Computer vision runs via client-side MediaPipe WASM and TFLite neural nets bundled locally under <code>/models/</code>. Raw camera frames pass directly from <code>HTMLVideoElement</code> into local WebAssembly memory textures and are immediately overwritten on the next animation frame.
                  </p>
                </div>

                <div className="privacy-card">
                  <div className="privacy-card-tag font-mono text-lime">F12 DEVTOOLS AUDIT</div>
                  <h4 className="font-mono" style={{ fontSize: 13, margin: "6px 0 10px", color: "var(--ink)" }}>Verify Network Traffic Live</h4>
                  <p className="privacy-text font-mono">
                    Press <code>F12</code> (or <code>Cmd+Option+I</code>) to open browser Developer Tools. Switch to the <strong>Network</strong> tab and filter by <strong>Fetch/XHR</strong>. Observe zero outgoing background requests while moving your head and body.
                  </p>
                </div>
              </div>

              <div className="privacy-card" style={{ marginTop: 16 }}>
                <div className="privacy-card-tag font-mono text-magenta">INDEPENDENT 3RD-PARTY AUDITS</div>
                <h4 className="font-mono" style={{ fontSize: 13, margin: "6px 0 10px", color: "var(--ink)" }}>
                  Recognized Third-Party Verification Tools
                </h4>
                <p className="privacy-text font-mono">
                  You can use public, neutral privacy and security scanners to inspect our domain and verify our claims:
                </p>
                <ul className="privacy-list font-mono">
                  <li>
                    <strong>Blacklight by The Markup:</strong> Non-profit consumer privacy inspector that scans for ad trackers, tracking cookies, keystroke/movement loggers, and session recorders (<a href="https://themarkup.org/blacklight" target="_blank" rel="noopener noreferrer">themarkup.org/blacklight</a>).
                  </li>
                  <li>
                    <strong>Mozilla Observatory &amp; SecurityHeaders:</strong> Verifies cryptographic and Content Security Policy headers to validate that egress is locked down (<a href="https://observatory.mozilla.org" target="_blank" rel="noopener noreferrer">observatory.mozilla.org</a>).
                  </li>
                  <li>
                    <strong>URLScan.io:</strong> Sandboxed execution logger that records every single network request, IP address, and connection made during a session (<a href="https://urlscan.io" target="_blank" rel="noopener noreferrer">urlscan.io</a>).
                  </li>
                </ul>
              </div>

              <div className="privacy-audit-footer font-mono">
                <span>Architecture: 100% Client-Side WebAssembly · Zero Cloud Dependency · Zero Egress</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="privacy-modal-footer font-mono">
          <div className="privacy-footer-status">
            <span className="privacy-pulse-dot" style={{ background: "var(--neon-lime)", boxShadow: "0 0 8px var(--neon-lime)" }} />
            <span>STATUS: 100% LOCAL IN-MEMORY · RUNS OFFLINE</span>
          </div>
          <button onClick={onClose} className="btn-primary" style={{ padding: "8px 20px", fontSize: 11, letterSpacing: "0.18em", borderRadius: 999 }}>
            UNDERSTOOD →
          </button>
        </div>
      </div>
    </div>
  );
}
